import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/auth/auth-context";
import { apiRequest } from "@/lib/api";
import { Mail, Lock, ArrowRight, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ROUTE_PATHS, getRootDomainUrl } from "@/routing/domainRoutes";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast({
        title: t("login.missingCredentials"),
        description: t("login.missingCredentialsDescription"),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email, password);
      const nextPath = (location.state as { from?: string } | null)?.from ?? ROUTE_PATHS.dashboardHome;
      navigate(nextPath, { replace: true });
    } catch (error) {
      toast({
        title: t("login.loginFailed"),
        description: t("login.loginFailedDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openForgotModal = () => {
    setResetEmail(email || "");
    setResetSuccess(null);
    setResetError(null);
    setForgotOpen(true);
  };

  const sendResetLink = async () => {
    if (!resetEmail.trim()) {
      setResetError(t("login.resetMissingEmail"));
      return;
    }

    setIsSendingReset(true);
    setResetError(null);
    try {
      const response = await apiRequest<{ success: boolean; message: string }>("/auth/forgot-password", {
        method: "POST",
        auth: false,
        body: { email: resetEmail.trim() },
      });
      setResetSuccess(response.message || t("login.resetSuccess"));
    } catch {
      setResetError(t("login.resetError"));
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-[45%] bg-primary flex-col justify-between p-12">
        <a href={getRootDomainUrl(ROUTE_PATHS.landing)} className="flex w-fit cursor-pointer items-center gap-2">
          <img src="/workscope-mark.svg" alt="WorkScope" className="h-8 w-8 object-contain" />
          <span className="text-2xl font-bold text-primary-foreground tracking-tight">WorkScope</span>
        </a>
        <div className="space-y-6">
          <h1 className="text-4xl font-bold text-primary-foreground leading-tight">
            {t("login.marketingTitleLine1")}<br />{t("login.marketingTitleLine2")}<br />
            <span className="text-accent">{t("login.marketingTitleAccent")}</span>
          </h1>
          <p className="text-primary-foreground/70 text-lg max-w-md leading-relaxed">
            {t("login.marketingCopy")}
          </p>
          <div className="grid grid-cols-2 gap-4 pt-4 max-w-md">
            {[
              t("login.features.photos"),
              t("login.features.signatures"),
              t("login.features.pdfs"),
              t("login.features.approvals"),
              t("login.features.billing"),
              t("login.features.mobile"),
            ].map((feat) => (
              <div key={feat} className="flex items-center gap-2 text-primary-foreground/80 text-sm">
                <div className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                {feat}
              </div>
            ))}
          </div>
        </div>
        <p className="text-primary-foreground/40 text-sm">
          {t("login.trustedBy")}
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm space-y-8">
          <a
            href={getRootDomainUrl(ROUTE_PATHS.landing)}
            className="lg:hidden mb-4 flex cursor-pointer items-center justify-center gap-2"
          >
            <img src="/workscope-mark.svg" alt="WorkScope" className="h-7 w-7 object-contain" />
            <span className="text-xl font-bold text-foreground">WorkScope</span>
          </a>
          <div className="text-center lg:text-left">
            <h2 className="text-2xl font-bold text-foreground">{t("login.welcomeBack")}</h2>
            <p className="text-muted-foreground mt-1">{t("login.signInWorkspace")}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("login.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  className="pl-9"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("login.password")}</Label>
                <button type="button" className="text-xs text-accent hover:underline" onClick={openForgotModal}>
                  {t("login.forgotPassword")}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="pl-9"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold gap-2"
            >
              {isSubmitting ? t("login.signingIn") : t("login.signIn")} <ArrowRight className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("login.resetPassword")}</DialogTitle>
            <DialogDescription>{t("login.resetPasswordDescription")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="reset-email">{t("login.email")}</Label>
            <Input
              id="reset-email"
              type="email"
              placeholder="you@company.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              disabled={isSendingReset}
            />
            {resetSuccess ? (
              <p className="text-sm text-green-600">{resetSuccess}</p>
            ) : null}
            {resetError ? (
              <p className="text-sm text-destructive">{resetError}</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setForgotOpen(false)} disabled={isSendingReset}>
              {t("login.cancel")}
            </Button>
            <Button onClick={sendResetLink} disabled={isSendingReset} className="bg-accent text-accent-foreground hover:bg-accent/90">
              {isSendingReset ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("login.sending")}
                </>
              ) : (
                t("login.sendResetLink")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
